/**
 * @file useMicGlow.ts
 * @brief The soft brand halo behind a small hold button that grows with the
 *        voice, for the recorder dock and the desktop widget.
 *
 * @details
 * DES 11-10-2026 ("mic glow when someone speaks", then "widget main glow add
 * nahin ho sakta"). ListeningMicButton's meter (fast attack, slow release,
 * eased), scaled up for a 36-40px button. Drives the element's style from a
 * requestAnimationFrame loop, so the page never re-renders per frame. Off
 * (and reset) whenever `active` is false.
 *
 * @author DES
 * @date 11-10-2026
 */

import { useEffect, useRef, type RefObject } from "react";

export function useMicGlow(levelRef: RefObject<number> | { current: number }, active: boolean) {
  const glowRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const glow = glowRef.current;
    if (!glow) return;
    const off = () => {
      glow.style.opacity = "0";
      glow.style.transform = "scale(1)";
    };
    if (!active) {
      off();
      return;
    }
    let smooth = 0;
    let raf = 0;
    const tick = () => {
      const raw = Math.max(0, Math.min(1, (levelRef.current ?? 0) / 100));
      smooth += (raw - smooth) * (raw > smooth ? 0.72 : 0.28);
      if (smooth < 0.008) {
        off();
      } else {
        const eased = Math.pow(smooth, 0.65);
        glow.style.opacity = String(0.12 + eased * 0.55);
        glow.style.transform = `scale(${1 + eased * 0.9})`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      off();
    };
  }, [levelRef, active]);
  return glowRef;
}
